import { useEffect, useState } from 'react';
import {
  configApiRef,
  fetchApiRef,
  useApi,
  type FetchApi,
} from '@backstage/core-plugin-api';

// keep in sync with plugins/scorecard/src/hooks/useCanEditTeam.ts
const permissionCache = new Map<string, Promise<boolean>>();

export function clearCanEditCache(): void {
  permissionCache.clear();
}

function fetchCanEdit(
  fetchApi: FetchApi,
  backendBase: string,
  machineId: string,
): Promise<boolean> {
  const cacheKey = `${backendBase}/${machineId}`;
  const cached = permissionCache.get(cacheKey);
  if (cached) return cached;
  const request = (async () => {
    const response = await fetchApi.fetch(
      `${backendBase}/teams/${machineId}/permissions`,
    );
    if (!response.ok) throw new Error(`permissions ${response.status}`);
    const data = await response.json();
    return Boolean(data.canEdit);
  })();
  permissionCache.set(cacheKey, request);
  request.catch(() => permissionCache.delete(cacheKey));
  return request;
}

export function useCanEditTeam(
  machineId: string,
  namespace: string = 'default',
): boolean | null {
  const configApi = useApi(configApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [result, setResult] = useState<{
    key: string;
    canEdit: boolean;
  } | null>(null);

  const readOnly =
    configApi.getOptionalBoolean('teamManagement.readOnly') ?? false;
  const backendBase = `${configApi.getString('backend.baseUrl')}/api/team-management`;
  const blocked = readOnly || !machineId || namespace !== 'default';
  const key = `${namespace}/${machineId}`;

  useEffect(() => {
    if (blocked) return undefined;
    let cancelled = false;
    (async () => {
      let canEdit = false;
      try {
        canEdit = await fetchCanEdit(fetchApi, backendBase, machineId);
      } catch {
        canEdit = false;
      }
      if (!cancelled) setResult({ key, canEdit });
    })();
    return () => {
      cancelled = true;
    };
  }, [blocked, key, machineId, backendBase, fetchApi]);

  if (blocked) return false;
  return result?.key === key ? result.canEdit : null;
}
